import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { isGuestWelcomeRoute as isGuestWelcomeRouteFromRegistry } from './audienceGuard';
import {
  exitOrGoBackViaService,
  navigationServiceNavigate,
  resetAuthenticatedHome,
  type NavigationHandle,
} from './navigationService';
import { useAuthStore } from '../auth/store';

export function isGuestWelcomeRoute(route_name: string | undefined): boolean {
  return isGuestWelcomeRouteFromRegistry(route_name);
}

export { getPreviousStackRouteName } from './navigationService';

/** Send authenticated users to root Main stack → MainTabs → Dashboard. */
export function resetToAuthenticatedDashboard(navigation: NavigationHandle): void {
  resetAuthenticatedHome(navigation);
}

/**
 * Navigate to guest welcome (`Main`) or, when signed in, to Dashboard.
 */
export function navigateGuestWelcomeOrDashboard(
  navigation: NavigationHandle,
  is_authenticated: boolean,
  params?: object,
): void {
  if (is_authenticated) {
    resetAuthenticatedHome(navigation);
    return;
  }
  navigation.navigate('Main', params);
}

export function exitOrGoBack(navigation: NavigationHandle, is_authenticated: boolean) {
  exitOrGoBackViaService(navigation, is_authenticated);
}

export function useExitOrGoBack() {
  const navigation = useNavigation();
  const is_authenticated = useAuthStore(s => s.isAuthenticated);
  return useCallback(
    () => exitOrGoBackViaService(navigation as unknown as NavigationHandle, is_authenticated),
    [navigation, is_authenticated],
  );
}

export function useRedirectAuthenticatedFromGuestWelcome() {
  const navigation = useNavigation();
  const is_focused = useIsFocused();
  const is_authenticated = useAuthStore(s => s.isAuthenticated);

  useEffect(() => {
    if (!is_focused || !is_authenticated) {
      return;
    }
    resetAuthenticatedHome(navigation as unknown as NavigationHandle);
  }, [is_focused, is_authenticated, navigation]);
}

export function useAuthNavigation() {
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList>>();
  const is_authenticated = useAuthStore(s => s.isAuthenticated);
  const base_navigate_ref = useRef(navigation.navigate);
  base_navigate_ref.current = navigation.navigate;

  const navigate = useCallback(
    (name: string, params?: object) => {
      navigationServiceNavigate(navigation as unknown as NavigationHandle, name, params, is_authenticated);
    },
    [navigation, is_authenticated],
  );

  return useMemo(
    () => ({
      ...navigation,
      navigate,
    }),
    [navigation, navigate],
  );
}
